// App shell: picks the screen for the current route, guards signed-in/onboarding screens, frames phone screens on wide
// windows, renders the toast host, and wires shell events (Android back, notification taps, tray, hotkey).
// Desktop, signed in: her room (K1) answers C, T, W, S, R and U1 itself; the other pages open as the phone screen in a
// panel over the room.
import { useEffect } from 'preact/hooks';
import { SCREENS } from './screens/registry';
import { Room, ROOM_ROUTES } from './screens/desktop';
import { ToastHost } from './components';
import { useApp, appStore, startCard, doneCard, undoneCard, rewardSeen, toast, dismissToast } from './store';
import { deskSession } from './lib/deskSession';
import { isBigReward, taskShort } from '@shared/rules';
import { useLayout } from './layout';
import { back, canGoBack, go, isOnboarding, openRoute, replace, routerStore, useScreen, type ScreenId } from './router';
import { desktop, exitApp, isDesktopShell, isMiniWindow, onNative } from './native';
import { homeScreen } from './home';
import { draftStore, setDraft } from './screens/onboarding/draft';
import s from './app.module.css';

/** Where a screen may be shown right now (signed out: onboarding only, in order; signed in: no onboarding). */
export function allowedScreen(id: ScreenId, signedIn: boolean): ScreenId {
  if (isMiniWindow()) return 'K2'; // the small window is always K2 (it says so itself when signed out)
  if (signedIn) return isOnboarding(id) ? homeScreen() : id;
  if (!isOnboarding(id)) return homeScreen();
  const d = draftStore.get();
  if (id !== 'O1' && !d.invite) return 'O1';
  if (['O8', 'O9', 'O10', 'O11'].includes(id) && !d.ch) return 'O7';
  if ((id === 'O10' || id === 'O11') && !d.first) return 'O8';
  return id;
}

export function App() {
  const screen = useScreen();
  const signedIn = useApp((st) => !!st.token);
  const booted = useApp((st) => st.booted);
  const layout = useLayout();

  // Keep the router on an allowed screen (replace, so Back never returns to a forbidden one).
  const allowed = booted ? allowedScreen(screen, signedIn) : screen;
  useEffect(() => { if (booted && allowed !== screen) replace(allowed); }, [booted, allowed, screen]);

  // Remember the onboarding step so a restart resumes there.
  useEffect(() => routerStore.subscribe((r) => {
    if (!appStore.get().token && isOnboarding(r.s) && draftStore.get().screen !== r.s) setDraft({ screen: r.s });
  }), []);

  useEffect(() => onNative((e) => {
    const st = appStore.get();
    if (e.type === 'back') {
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
      } else if (canGoBack()) back();
      else exitApp();
    } else if (e.type === 'route' && st.token && st.booted) {
      // Before boot, main.tsx keeps the route and opens it after the session is back (a cold-start tap wins).
      openRoute(e.route);
    } else if (e.type === 'talk' && st.token) {
      // The chat listens for xm:talk; when it isn't mounted yet (e.g. hotkey from Us/Me), wait until it is.
      const mounted = !!document.querySelector('[data-screen="C"]');
      go('C');
      if (mounted) window.dispatchEvent(new CustomEvent('xm:talk'));
      else setTimeout(() => window.dispatchEvent(new CustomEvent('xm:talk')), 300);
    } else if (e.type === 'start-next' && st.state) {
      const next = st.state.plan.cards.find((c) => c.st === 'todo');
      if (!next) { go('C'); return; }
      void startCard(next.id, null, 'next'); // the menu bar's UP NEXT card
      if (isDesktopShell()) desktop.openMini(); else go('W', { cur: next.id, from: 'T' });
    } else if (e.type === 'done-now' && st.state) {
      // Menu bar / tray Done on the card being worked on. When she is on screen in the small window, she shows it
      // there; with Xiaoman in front, the light toast with Undo (a big one opens her celebration in the room).
      const cur = deskSession(st.state);
      if (!cur) return;
      void doneCard(cur.id, { from: 'work-chat' }).then(async (rw) => {
        if (!rw || (await desktop.pipState())?.shown) return;
        if (isBigReward(rw)) { openRoute('R:' + cur.id); return; }
        toast(`${taskShort(rw.title)}: done · +${rw.gained} points`, { undo: () => { dismissToast(); void undoneCard(cur.id); } });
        void rewardSeen();
      });
    }
  }), []);

  if (!booted) return <div class={s.root} aria-busy="true" />;

  const id: ScreenId = isMiniWindow() && signedIn ? 'K2' : allowed;
  const desk = layout === 'desktop' && signedIn && id !== 'K2';
  const Comp = SCREENS[id];
  if (desk) {
    // The room stays mounted across its own routes and under the panel, so her loop and the subtitles never restart.
    const panel = (ROOM_ROUTES as readonly string[]).includes(id) ? null : <Comp key={id} />;
    return <div class={s.root} data-layout={layout}><div class={s.full}><Room panel={panel} /></div></div>;
  }
  const inner = <><Comp key={id} /><ToastHost /></>;
  return (
    <div class={s.root} data-layout={layout}>
      <div class={layout === 'desktop' && id !== 'K2' ? s.frame : s.full}>{inner}</div>
    </div>
  );
}
